import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Package, CheckCircle2, Clock, Truck, ArrowLeft, AlertCircle, Search } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'design_review', label: 'Design Review', icon: Clock, description: 'We are reviewing your design placement' },
  { key: 'production', label: 'In Production', icon: Package, description: 'Your item is being produced' },
  { key: 'shipped', label: 'Shipped', icon: Truck, description: 'Your order is on its way' },
  { key: 'completed', label: 'Completed', icon: CheckCircle2, description: 'Order delivered' },
];

function getStatusIndex(status) {
  return STATUS_STEPS.findIndex(s => s.key === status);
}

// Search page shown when no orderId in URL
function TrackSearchPage() {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    let q = query.trim().toUpperCase();
    if (!q) return;
    // Auto-prepend ORD- if user just typed the 6-char code
    if (/^[A-Z0-9]{6}$/.test(q)) q = `ORD-${q}`;
    setSearching(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/orders/track/${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error('Order not found');
      const order = await res.json();
      navigate(`/track/${order.id}`, { replace: true });
    } catch {
      setError('No order found. Make sure you enter the full order number, e.g. ORD-6D1259');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead title="Track Your Order" description="Track your custom print order" path="/track" />
      <Navigation />
      <section className="pt-32 pb-16">
        <div className="max-w-lg mx-auto px-4 sm:px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center mx-auto mb-6">
            <Package className="w-6 h-6 text-[#00E5FF]" />
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white tracking-tight mb-3">Track Your Order</h1>
          <p className="text-sm text-slate-500 mb-8">Enter your order number to check the status of your custom print order.</p>

          <form onSubmit={handleSearch} className="flex gap-2" data-testid="track-search-form">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="track-search-input"
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value.toUpperCase())}
                placeholder="ORD-XXXXXX"
                className="w-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl pl-11 pr-4 py-4 text-sm text-[#0A0A0A] dark:text-white placeholder:text-slate-400 focus:outline-none focus:border-[#00E5FF]/50 font-mono uppercase tracking-wider"
              />
            </div>
            <button type="submit" disabled={searching || !query.trim()} data-testid="track-search-btn"
              className="bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-6 rounded-xl font-semibold text-sm hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all disabled:opacity-50">
              {searching ? '...' : 'Track'}
            </button>
          </form>

          {error && (
            <div className="mt-4 flex items-center gap-2 justify-center text-sm text-rose-400" data-testid="track-search-error">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          <div className="mt-6 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-xl p-4">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">Your order number looks like this:</p>
            <span className="font-mono text-base font-bold text-[#0A0A0A] dark:text-white tracking-widest">ORD-XXXXXX</span>
            <p className="text-[10px] text-slate-400 mt-2">It was included in your confirmation email after checkout.</p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function OrderDetailPage({ orderId }) {
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollingDone, setPollingDone] = useState(false);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 8) { setPollingDone(true); return; }
    try {
      const res = await fetch(`${API}/api/checkout/status/${sid}`);
      if (!res.ok) throw new Error('Failed to check status');
      const data = await res.json();
      if (data.payment_status === 'paid' && data.order) {
        setOrder(data.order);
        setPollingDone(true);
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2500);
    } catch {
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 3000);
    }
  }, []);

  useEffect(() => {
    if (sessionId && !pollingDone) {
      pollPaymentStatus(sessionId);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/api/orders/track/${orderId}`);
        if (!res.ok) throw new Error('Order not found');
        setOrder(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, sessionId, pollingDone, pollPaymentStatus]);

  if (loading && !order) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">{sessionId ? 'Confirming your payment...' : 'Loading order...'}</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
        <Navigation />
        <div className="pt-32 text-center px-4">
          <AlertCircle className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-2xl font-bold text-[#0A0A0A] dark:text-white mb-2">Order not found</h1>
          <p className="text-sm text-slate-500 mb-6">We couldn't find this order. Please check your order number.</p>
          <Link to="/track" className="text-sm text-[#00E5FF] hover:underline inline-flex items-center gap-2"><Search className="w-4 h-4" />Try searching again</Link>
        </div>
      </div>
    );
  }

  const currentIdx = getStatusIndex(order.order_status);
  const isPaid = order.payment_status === 'paid';
  const isCancelled = order.order_status === 'cancelled';

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead title={`Order ${order.order_number} | Track`} description="Track your custom print order" path={`/track/${orderId}`} />
      <Navigation />

      <section className="pt-24 pb-16 md:pt-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/track" className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-[#00E5FF] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />Track another order
          </Link>

          {/* Header */}
          <div className="mb-8">
            {isPaid && !isCancelled && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-lg mb-4" data-testid="payment-success-badge">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-green-500">Payment confirmed</span>
              </div>
            )}
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#0A0A0A] dark:text-white tracking-tight" data-testid="order-number">
              {order.order_number}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Placed {new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>

          {/* Status Timeline */}
          {!isCancelled && isPaid && (
            <div className="mb-10 p-6 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-2xl" data-testid="status-timeline">
              <h2 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-slate-500 mb-6">Order Status</h2>
              <div className="space-y-0">
                {STATUS_STEPS.map((step, i) => {
                  const Icon = step.icon;
                  const isActive = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  return (
                    <div key={step.key} className="flex gap-4" data-testid={`status-step-${step.key}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                          isCurrent ? 'border-[#00E5FF] bg-[#00E5FF]/10' : isActive ? 'border-green-500 bg-green-500/10' : 'border-slate-200 dark:border-white/10'
                        }`}>
                          <Icon className={`w-4 h-4 ${isCurrent ? 'text-[#00E5FF]' : isActive ? 'text-green-500' : 'text-slate-300 dark:text-slate-600'}`} />
                        </div>
                        {i < STATUS_STEPS.length - 1 && (
                          <div className={`w-0.5 h-8 ${isActive ? 'bg-green-500/40' : 'bg-slate-200 dark:bg-white/5'}`} />
                        )}
                      </div>
                      <div className="pb-6">
                        <p className={`text-sm font-semibold ${isCurrent ? 'text-[#00E5FF]' : isActive ? 'text-[#0A0A0A] dark:text-white' : 'text-slate-400 dark:text-slate-600'}`}>{step.label}</p>
                        {isCurrent && <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isCancelled && (
            <div className="mb-10 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-center">
              <AlertCircle className="w-6 h-6 text-rose-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-rose-400">This order has been cancelled</p>
            </div>
          )}

          {/* Order Details */}
          <div className="bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-2xl p-6" data-testid="order-details">
            <h2 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">Order Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Product</span>
                <span className="text-[#0A0A0A] dark:text-white font-medium">{order.product_title}</span>
              </div>
              {order.selected_color && (
                <div className="flex justify-between text-sm items-center">
                  <span className="text-slate-500">Colour</span>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border border-slate-200 dark:border-white/20" style={{ backgroundColor: order.selected_color.hex_code }} />
                    <span className="text-[#0A0A0A] dark:text-white">{order.selected_color.name}</span>
                  </div>
                </div>
              )}
              {order.selected_options?.map((opt, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-500">{opt.label}</span>
                  {opt.price_modifier > 0 && <span className="text-slate-400">+£{opt.price_modifier.toFixed(2)}</span>}
                </div>
              ))}
              {order.discount_percent > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-500">Discount ({order.discount_percent}%)</span>
                  <span className="text-green-500">Applied</span>
                </div>
              )}
              <div className="border-t border-slate-200 dark:border-white/10 pt-3 flex justify-between">
                <span className="text-sm font-semibold text-[#0A0A0A] dark:text-white">Total</span>
                <span className="font-['Outfit'] text-xl font-bold text-[#0A0A0A] dark:text-white" data-testid="order-total">£{order.total_price?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Design Preview */}
          {order.cropped_logo_path && (
            <div className="mt-6 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/10 rounded-2xl p-6" data-testid="design-preview">
              <h2 className="font-['Outfit'] text-sm font-bold uppercase tracking-widest text-slate-500 mb-4">Your Design</h2>
              <div className="w-24 h-24 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden bg-white">
                <img src={`${API}/api/files/${order.cropped_logo_path}`} alt="Your design" className="w-full h-full object-contain" />
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default function OrderTrackPage() {
  const { orderId } = useParams();
  if (!orderId) return <TrackSearchPage />;
  return <OrderDetailPage orderId={orderId} />;
}
